if (!customElements.get('quick-add-modal')) {
  document.addEventListener(
    'pointerdown',
    (event) => {
      const trigger = event.target.closest?.('.pm-quick-add__trigger');
      if (trigger) trigger.dataset.pmPointerActivated = 'true';
    },
    true
  );

  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.addEventListener('keyup', this.handleNestedEscape.bind(this), true);

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      connectedCallback() {
        if (!this.moved) {
          document.querySelectorAll('quick-add-modal').forEach((modal) => {
            if (modal === this || modal.id !== this.id) return;
            if (modal.hasAttribute('open')) modal.hide(true);
            modal.remove();
          });
        }

        super.connectedCallback();
      }

      handleNestedEscape(event) {
        if (event.code.toUpperCase() !== 'ESCAPE' || this.dataset.pmNestedEscape !== 'true') return;
        event.preventDefault();
        event.stopImmediatePropagation();
        delete this.dataset.pmNestedEscape;
      }

      hide(preventFocus = false) {
        const opener = this.openedBy;
        const shouldRestoreFocus = !preventFocus && this.restoreOpenerFocus !== false;
        this.classList.remove('pm-is-open');
        opener?.setAttribute('aria-expanded', 'false');
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (!shouldRestoreFocus) this.openedBy = null;
        super.hide();
        if (!shouldRestoreFocus) {
          opener?.classList.remove('focused');
          opener?.blur();
        }
        this.restoreOpenerFocus = undefined;
      }

      show(opener) {
        this.restoreOpenerFocus = opener?.dataset.pmPointerActivated !== 'true';
        if (opener) delete opener.dataset.pmPointerActivated;
        this.loadController?.abort();
        const loadController = new AbortController();
        const requestToken = Symbol('quick-add-request');
        this.loadController = loadController;
        this.activeRequestToken = requestToken;

        this.classList.remove('pm-is-open');
        opener.disabled = true;
        opener.setAttribute('aria-disabled', true);
        opener.setAttribute('aria-expanded', 'false');
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'), { signal: loadController.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`Product details returned ${response.status}.`);
            return response.text();
          })
          .then((responseText) => {
            if (this.activeRequestToken !== requestToken) return;
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');
            if (!productElement) throw new Error('Product details were not found in the response.');
            const productTitle = productElement.querySelector('.product__title h1, .product__title a')?.textContent.trim();

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);
            const dialog = this.querySelector('[role="dialog"]');
            if (dialog && productTitle) dialog.setAttribute('aria-label', `Quick add ${productTitle}`);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

            super.show(opener);
            opener.setAttribute('aria-expanded', 'true');
            window.requestAnimationFrame(() => this.classList.add('pm-is-open'));
          })
          .catch((error) => {
            if (error.name === 'AbortError' || this.activeRequestToken !== requestToken) return;
            console.error('Quick Add could not load the selected product.', error);
            this.showLoadError(opener);
          })
          .finally(() => {
            opener.disabled = false;
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
            if (this.activeRequestToken === requestToken) {
              this.activeRequestToken = null;
              this.loadController = null;
            }
          });
      }

      showLoadError(opener) {
        const surface = document.createElement('div');
        const heading = document.createElement('h2');
        const message = document.createElement('p');
        const link = document.createElement('a');

        surface.className = 'pm-quick-add-error';
        surface.setAttribute('role', 'alert');
        heading.textContent = 'This meal could not be loaded';
        message.textContent = 'Try again, or open the full product page to finish your order.';
        link.className = 'button button--primary';
        link.href = opener.getAttribute('data-product-url') || '/collections/all';
        link.textContent = 'View full product';
        surface.append(heading, message, link);
        this.modalContent.replaceChildren(surface);

        super.show(opener);
        opener.setAttribute('aria-expanded', 'true');
        window.requestAnimationFrame(() => this.classList.add('pm-is-open'));
      }

      preprocessHTML(productElement) {
        productElement.classList.add('pm-quick-add-product');
        productElement.querySelectorAll('[data-pm-quick-add-only]').forEach((element) => {
          element.hidden = false;
        });
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());

      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const drawerToken = (this.id || 'drawer').replace(/[^a-zA-Z0-9_-]/g, '-');
        const productToken = productElement.dataset.productId || 'product';
        const newId = `quickadd-${drawerToken}-${productToken}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
